import { useState } from "react";
import { useAuthContext } from "./useAuthContext";
import { useAccountContext } from "./useAccountContext";
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export const useShifts = () => {

    const { t } = useTranslation();
    const [error, setError] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const { account } = useAccountContext();
    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        // console.log('Set Bearer Token')
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            // console.log('onLeave Refresh Access Token Expired!!!')
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!winAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    const getSchedulePeriods = async () => {
        setIsLoading(true);
        let resp = [];
        await axiosInstance.get(`${clientApi}api/scheduleperiods`, {
            params: { username: username }
        })
            .then((response) => {
                resp = response.data;
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                setError(error);
            });
        return resp;
    };

    const getSchedulePeriod = async (id) => {
        setIsLoading(true);
        let resp = undefined;
        await axiosInstance.get(`${clientApi}api/scheduleperiods/${id}`, {
            params: { username: username }
        })
            .then((response) => {
                resp = response.data;
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                setError(error);
            });
        return resp;
    };

    const saveSchedulePeriod = async (payload) => {
        setIsLoading(true);
        let resp = undefined;
        await axiosInstance.post(`${clientApi}api/scheduleperiods/Save`, payload, {
            params: { username: username }
        })
            .then((response) => {
                resp = response.data;
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                setError(error);
            });
        return resp;
    };

    const deleteSchedulePeriod = async (id) => {
        setIsLoading(true);
        let success = false;
        await axiosInstance.delete(`${clientApi}api/scheduleperiods/${id}`, {
            params: { username: username }
        })
            .then(() => {
                success = true;
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                setError(error);
            });
        return success;
    };

    const getShiftAssignments = async (schedulePeriodId) => {
        setIsLoading(true);
        let assignments = [];
        await axiosInstance.get(`${clientApi}api/shiftsAssignments`, {
            params: { username, schedulePeriodId }
        })
            .then((response) => {
                assignments = Array.isArray(response.data) ? response.data : [];
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                setError(error);
            });
        return assignments;
    };

    const saveShiftAssignment = async (payload) => {
        setIsLoading(true);
        let assignment = undefined;
        await axiosInstance.post(`${clientApi}api/shiftsAssignments/Save`, payload, {
            params: { username }
        })
            .then((response) => {
                assignment = response.data;
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                setError(error);
            });
        return assignment;
    };

    const deleteShiftAssignment = async (id) => {
        setIsLoading(true);
        let success = false;
        await axiosInstance.delete(`${clientApi}api/shiftsAssignments/${id}`, {
            params: { username }
        })
            .then(() => {
                success = true;
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                setError(error);
            });
        return success;
    };

    const getShiftTemplates = async () => {
        setIsLoading(true)
        let resp = {};
        await axiosInstance.get(`${clientApi}api/shifttemplates`, {
            params: {
                username: username,
            }
        })
            .then((response) => {
                // * This gets from OASISPARAM which is always single line
                setIsLoading(false)
                // console.log(response)
                resp = response.data;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
        return resp;
    };

    const getShiftTemplateBreaks = async (id) => {
        const response = await axiosInstance.get(`${clientApi}api/shifttemplates/${id}/breaks`, {
            params: { username: username }
        });
        return response.data;
    };


    const saveShiftTemplate = async (payload) => {
        setIsLoading(true)
        let success = false;
        await axiosInstance.post(`${clientApi}api/shifttemplates/CreateTemplate`, payload, {
            params: {
                username: username,
            }
        })
            .then(() => {
                setIsLoading(false)
                success = true;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
        return success;
    };
    const updateShiftTemplate = async (id, payload) => {
        setIsLoading(true)
        let success = false;
        await axiosInstance.put(`${clientApi}api/shifttemplates/UpdateTemplate/${id}`, payload, {
            params: {
                username: username,
            }
        })
            .then(() => {
                setIsLoading(false)
                success = true;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
        return success;
    };


    const deleteShiftTemplate = async (id) => {
        setIsLoading(true)
        let success = false;
        await axiosInstance.delete(`${clientApi}api/shifttemplates/DeleteTemplate/${id}`, {
            params: {
                username: username,
            }
        })
            .then(() => {
                setIsLoading(false)
                success = true;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
        return success;
    };

    const getAuthEmployeesWithLeavesFY = async (logedInId = account?.employeeId, company = '', dept = '') => {
        if (logedInId == null) return undefined;
        setIsLoading(true)
        let resp = undefined;
        await axiosInstance.get(`${clientApi}api/shiftsAssignments/GetEmployeesWithLeavesForShifts`, {
            params: {
                sUsername: username,
                sUserId: logedInId,
                sCompany: company,
                sDept: dept
            }
        })
            .then((res) => {
                setIsLoading(false)
                resp = res.data;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
        return resp;
    };


    return { getSchedulePeriods, getSchedulePeriod, saveSchedulePeriod, deleteSchedulePeriod, getShiftAssignments, saveShiftAssignment, deleteShiftAssignment, getShiftTemplates, getShiftTemplateBreaks, saveShiftTemplate, deleteShiftTemplate, updateShiftTemplate, getAuthEmployeesWithLeavesFY, error, isLoading }
}